import React, { useState, useEffect } from 'react';
import {
  AppSettings,
  FontFamily,
  MirrorMode,
  MarkerSide,
  TextAlign,
  TextColumnWidth,
  TextColorPreset,
} from '../state/types';
import { ModelManager, ModelProgress, ModelStatus } from '../voice/model_manager';
import { X, RotateCcw, Download, Trash2, Cpu, Type, Layers, Activity, Info } from 'lucide-react';

interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  onUpdateSettings: (newSettings: Partial<AppSettings>) => void;
  onResetSettings: () => void;
  modelManager: ModelManager;
  onOpenDiagnostics: () => void;
}

export const SettingsDrawer: React.FC<SettingsDrawerProps> = ({
  isOpen,
  onClose,
  settings,
  onUpdateSettings,
  onResetSettings,
  modelManager,
  onOpenDiagnostics,
}) => {
  const [activeTab, setActiveTab] = useState<'text' | 'presentation' | 'voice' | 'about'>('text');
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [modelStatus, setModelStatus] = useState<ModelStatus>(modelManager.getStatus());
  const [modelProgress, setModelProgress] = useState<ModelProgress | null>(null);
  const [isCached, setIsCached] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Enumerate microphone devices
      if (navigator.mediaDevices?.enumerateDevices) {
        navigator.mediaDevices.enumerateDevices().then((devices) => {
          setAudioDevices(devices.filter((d) => d.kind === 'audioinput'));
        });
      }
      // Check model cache status
      modelManager.isModelCached().then(setIsCached);
      setModelStatus(modelManager.getStatus());
    }
  }, [isOpen, modelManager]);

  if (!isOpen) return null;

  const handleDownloadModel = async () => {
    setModelProgress({
      phase: 'downloading',
      progressFraction: 0,
      downloadedBytes: 0,
      totalBytes: 32289650,
      currentFile: '',
    });
    const success = await modelManager.downloadAndVerify((p) => {
      setModelProgress(p);
      if (p.phase === 'ready') {
        setModelStatus('ready');
        setIsCached(true);
      } else if (p.phase === 'error') {
        setModelStatus('error');
      }
    });
    if (success) {
      setIsCached(true);
      setModelStatus('ready');
    }
  };

  const handleRemoveModel = async () => {
    await modelManager.removeModel();
    setIsCached(false);
    setModelStatus('not_loaded');
    setModelProgress(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-xs transition-opacity animate-fadeIn">
      <div className="w-full max-w-md bg-neutral-900 border-l border-neutral-800 h-full flex flex-col shadow-2xl text-neutral-200 select-none">
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800">
          <h2 className="text-lg font-semibold text-white">Broadcast Settings</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-800 rounded-lg text-neutral-400 hover:text-white transition"
            aria-label="Close settings drawer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-neutral-800 px-4 bg-neutral-950/50 text-xs font-medium">
          <button
            onClick={() => setActiveTab('text')}
            className={`flex items-center space-x-1.5 px-3 py-3 border-b-2 transition ${
              activeTab === 'text' ? 'border-blue-500 text-white' : 'border-transparent text-neutral-400 hover:text-neutral-200'
            }`}
          >
            <Type className="w-3.5 h-3.5" />
            <span>Text</span>
          </button>

          <button
            onClick={() => setActiveTab('presentation')}
            className={`flex items-center space-x-1.5 px-3 py-3 border-b-2 transition ${
              activeTab === 'presentation' ? 'border-blue-500 text-white' : 'border-transparent text-neutral-400 hover:text-neutral-200'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Display</span>
          </button>

          <button
            onClick={() => setActiveTab('voice')}
            className={`flex items-center space-x-1.5 px-3 py-3 border-b-2 transition ${
              activeTab === 'voice' ? 'border-blue-500 text-white' : 'border-transparent text-neutral-400 hover:text-neutral-200'
            }`}
          >
            <Cpu className="w-3.5 h-3.5" />
            <span>Voice</span>
          </button>

          <button
            onClick={() => setActiveTab('about')}
            className={`flex items-center space-x-1.5 px-3 py-3 border-b-2 transition ${
              activeTab === 'about' ? 'border-blue-500 text-white' : 'border-transparent text-neutral-400 hover:text-neutral-200'
            }`}
          >
            <Info className="w-3.5 h-3.5" />
            <span>About</span>
          </button>
        </div>

        {/* Tab Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* TAB 1: TEXT & TYPOGRAPHY */}
          {activeTab === 'text' && (
            <div className="space-y-5">
              {/* Font Family */}
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                  Arabic Typeface
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'sans', label: 'Noto Sans' },
                    { id: 'naskh', label: 'Noto Naskh' },
                    { id: 'amiri', label: 'Amiri' },
                  ].map((font) => (
                    <button
                      key={font.id}
                      onClick={() => onUpdateSettings({ fontFamily: font.id as FontFamily })}
                      className={`px-3 py-2 text-xs rounded-lg border transition text-center ${
                        settings.fontFamily === font.id
                          ? 'border-blue-500 bg-blue-950/40 text-white font-bold'
                          : 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-750'
                      }`}
                    >
                      {font.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Size */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-neutral-400 font-semibold uppercase tracking-wider">Font Size</span>
                  <span className="font-mono text-white">{settings.fontSize} px</span>
                </div>
                <input
                  type="range"
                  min="24"
                  max="400"
                  value={settings.fontSize}
                  onChange={(e) => onUpdateSettings({ fontSize: parseInt(e.target.value, 10) })}
                  className="w-full accent-blue-500"
                />
              </div>

              {/* Line Height */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-neutral-400 font-semibold uppercase tracking-wider">Line Height</span>
                  <span className="font-mono text-white">{settings.lineHeight.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min="1.2"
                  max="2.2"
                  step="0.05"
                  value={settings.lineHeight}
                  onChange={(e) => onUpdateSettings({ lineHeight: parseFloat(e.target.value) })}
                  className="w-full accent-blue-500"
                />
              </div>

              {/* Text Alignment */}
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                  Alignment
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['start', 'center'] as TextAlign[]).map((align) => (
                    <button
                      key={align}
                      onClick={() => onUpdateSettings({ textAlign: align })}
                      className={`px-3 py-2 text-xs rounded-lg border transition capitalize ${
                        settings.textAlign === align
                          ? 'border-blue-500 bg-blue-950/40 text-white font-bold'
                          : 'border-neutral-700 bg-neutral-800 text-neutral-300'
                      }`}
                    >
                      {align === 'start' ? 'Start (Right in RTL)' : 'Center'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Column Width */}
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                  Column Width
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['narrow', 'normal', 'wide'] as TextColumnWidth[]).map((w) => (
                    <button
                      key={w}
                      onClick={() => onUpdateSettings({ textColumnWidth: w })}
                      className={`px-3 py-2 text-xs rounded-lg border transition capitalize ${
                        settings.textColumnWidth === w
                          ? 'border-blue-500 bg-blue-950/40 text-white font-bold'
                          : 'border-neutral-700 bg-neutral-800 text-neutral-300'
                      }`}
                    >
                      {w}
                    </button>
                  ))}
                </div>
              </div>

              {/* Text Color Preset */}
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                  Text Color Preset
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'white', label: 'Crisp White', color: '#ffffff' },
                    { id: 'warm', label: 'Warm White', color: '#f5f5dc' },
                    { id: 'yellow', label: 'Studio Yellow', color: '#ffff00' },
                  ].map((c) => (
                    <button
                      key={c.id}
                      onClick={() => onUpdateSettings({ textColorPreset: c.id as TextColorPreset })}
                      className={`flex items-center justify-center space-x-1.5 px-3 py-2 text-xs rounded-lg border transition ${
                        settings.textColorPreset === c.id
                          ? 'border-blue-500 bg-blue-950/40 text-white font-bold'
                          : 'border-neutral-700 bg-neutral-800 text-neutral-300'
                      }`}
                    >
                      <span className="w-3 h-3 rounded-full border border-neutral-600" style={{ backgroundColor: c.color }} />
                      <span>{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: PRESENTATION & OVERLAYS */}
          {activeTab === 'presentation' && (
            <div className="space-y-5">
              {/* Mirror Mode */}
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                  Mirroring
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(['off', 'horizontal', 'vertical', 'both'] as MirrorMode[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => onUpdateSettings({ mirrorMode: m })}
                      className={`px-2.5 py-2 text-xs rounded-lg border transition capitalize ${
                        settings.mirrorMode === m
                          ? 'border-blue-500 bg-blue-950/40 text-white font-bold'
                          : 'border-neutral-700 bg-neutral-800 text-neutral-300'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Reading Marker */}
              <div className="p-4 bg-neutral-950 rounded-lg border border-neutral-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">Reading Marker</span>
                  <input
                    type="checkbox"
                    checked={settings.markerEnabled}
                    onChange={(e) => onUpdateSettings({ markerEnabled: e.target.checked })}
                    className="accent-blue-500 h-4 w-4"
                  />
                </div>

                {settings.markerEnabled && (
                  <>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-neutral-400">Color</span>
                      <input
                        type="color"
                        value={settings.markerColor}
                        onChange={(e) => onUpdateSettings({ markerColor: e.target.value })}
                        className="w-7 h-7 bg-transparent border-0 cursor-pointer"
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-neutral-400">
                        <span>Side</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1">
                        {(['auto', 'left', 'right'] as MarkerSide[]).map((side) => (
                          <button
                            key={side}
                            onClick={() => onUpdateSettings({ markerSide: side })}
                            className={`py-1 text-xs rounded border capitalize ${
                              settings.markerSide === side
                                ? 'border-blue-500 bg-blue-950 text-white'
                                : 'border-neutral-700 text-neutral-300'
                            }`}
                          >
                            {side}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Focus Area & Focus Lines */}
              <div className="p-4 bg-neutral-950 rounded-lg border border-neutral-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">Focus Band Geometry</span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-neutral-400">Position</span>
                    <span className="font-mono text-white">{settings.focusAreaPosition}%</span>
                  </div>
                  <input
                    type="range"
                    min="20"
                    max="80"
                    value={settings.focusAreaPosition}
                    onChange={(e) => onUpdateSettings({ focusAreaPosition: parseInt(e.target.value, 10) })}
                    className="w-full accent-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-neutral-400">Height</span>
                    <span className="font-mono text-white">{settings.focusAreaHeight}%</span>
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="45"
                    value={settings.focusAreaHeight}
                    onChange={(e) => onUpdateSettings({ focusAreaHeight: parseInt(e.target.value, 10) })}
                    className="w-full accent-blue-500"
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-neutral-800">
                  <span className="text-xs text-neutral-300">Focus Lines</span>
                  <input
                    type="checkbox"
                    checked={settings.focusLinesEnabled}
                    onChange={(e) => onUpdateSettings({ focusLinesEnabled: e.target.checked })}
                    className="accent-blue-500 h-4 w-4"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-300">Focus Mask (Gradient Dim)</span>
                  <input
                    type="checkbox"
                    checked={settings.focusMaskEnabled}
                    onChange={(e) => onUpdateSettings({ focusMaskEnabled: e.target.checked })}
                    className="accent-blue-500 h-4 w-4"
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: VOICE FOLLOW & STT */}
          {activeTab === 'voice' && (
            <div className="space-y-5">
              {/* Mic Device */}
              <div>
                <label className="block text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                  Microphone Input
                </label>
                <select
                  value={settings.micDeviceId}
                  onChange={(e) => onUpdateSettings({ micDeviceId: e.target.value })}
                  className="w-full px-3 py-2 bg-neutral-950 border border-neutral-700 rounded-lg text-xs text-white outline-hidden focus:border-blue-500"
                >
                  <option value="">Default System Microphone</option>
                  {audioDevices.map((dev) => (
                    <option key={dev.deviceId} value={dev.deviceId}>
                      {dev.label || `Microphone (${dev.deviceId.slice(0, 8)}...)`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Provisional toggle */}
              <div className="flex items-center justify-between p-3 bg-neutral-950 rounded-lg border border-neutral-800">
                <div>
                  <div className="text-xs font-medium text-white">Provisional Updates</div>
                  <div className="text-[11px] text-neutral-500">Enables smooth rolling window updates</div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.voiceProvisionalEnabled}
                  onChange={(e) => onUpdateSettings({ voiceProvisionalEnabled: e.target.checked })}
                  className="accent-blue-500 h-4 w-4"
                />
              </div>

              {/* Speech Model Management */}
              <div className="p-4 bg-neutral-950 rounded-lg border border-neutral-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-neutral-300 uppercase tracking-wider">Local Speech Model</span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      isCached || modelStatus === 'ready'
                        ? 'bg-emerald-950 text-emerald-300 border border-emerald-800'
                        : 'bg-neutral-800 text-neutral-400'
                    }`}
                  >
                    {isCached || modelStatus === 'ready' ? 'Installed (Offline Ready)' : 'Not Installed'}
                  </span>
                </div>

                <p className="text-xs text-neutral-400">
                  Moonshine Tiny Arabic (~32 MB). Runs 100% locally via WebAssembly with zero cloud data transfer.
                </p>

                {modelProgress && modelProgress.phase === 'downloading' && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-neutral-400">
                      <span>Downloading {modelProgress.currentFile}...</span>
                      <span>{(modelProgress.progressFraction * 100).toFixed(0)}%</span>
                    </div>
                    <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-150"
                        style={{ width: `${modelProgress.progressFraction * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2 pt-2">
                  {!isCached && modelStatus !== 'ready' ? (
                    <button
                      onClick={handleDownloadModel}
                      disabled={modelProgress?.phase === 'downloading'}
                      className="flex-1 flex items-center justify-center space-x-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition disabled:opacity-50"
                    >
                      <Download className="w-4 h-4" />
                      <span>Download Model (~32 MB)</span>
                    </button>
                  ) : (
                    <button
                      onClick={handleRemoveModel}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-red-950/40 border border-red-900/60 hover:bg-red-900/50 text-red-300 rounded-lg text-xs transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Remove Model</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Diagnostics Button */}
              <div>
                <button
                  onClick={onOpenDiagnostics}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-neutral-800 hover:bg-neutral-750 text-neutral-200 rounded-lg text-xs font-medium transition border border-neutral-700"
                >
                  <Activity className="w-4 h-4 text-blue-400" />
                  <span>Open Engine Diagnostics Inspector</span>
                </button>
              </div>
            </div>
          )}

          {/* TAB 4: ABOUT & ATTRIBUTION */}
          {activeTab === 'about' && (
            <div className="space-y-5 text-xs text-neutral-300">
              <div className="p-4 bg-neutral-950 rounded-lg border border-neutral-800 space-y-2">
                <h3 className="font-bold text-white text-sm">TP Arabic Teleprompter</h3>
                <p className="text-neutral-400">
                  Version 1.2.0 • Local-First Broadcast Teleprompter for Professional Studios.
                </p>
                <div className="inline-block bg-emerald-950/80 text-emerald-300 border border-emerald-800/80 px-2 py-0.5 rounded text-[11px] font-semibold">
                  Zero Cloud Data Transfer (INV-1 Enforced)
                </div>
              </div>

              {/* Attribution Requirement from Moonshine License (§11.2) */}
              <div className="p-4 bg-neutral-950 rounded-lg border border-neutral-800 space-y-2">
                <div className="font-bold text-white uppercase tracking-wider text-[11px]">Speech Recognition</div>
                <div className="text-sm font-semibold text-blue-400">Powered by Moonshine AI</div>
                <p className="text-neutral-400 text-[11px]">
                  This Moonshine AI Model is licensed under the Moonshine AI Community License, Copyright © Moonshine AI Ltd. All Rights Reserved.
                </p>
              </div>

              {/* Font Attribution */}
              <div className="p-4 bg-neutral-950 rounded-lg border border-neutral-800 space-y-2">
                <div className="font-bold text-white uppercase tracking-wider text-[11px]">Typography</div>
                <p className="text-neutral-400 text-[11px]">
                  Self-hosted typefaces Noto Sans Arabic, Noto Naskh Arabic, and Amiri are licensed under the SIL Open Font License (OFL-1.1).
                </p>
              </div>

              {/* Reset to Factory Defaults */}
              <div className="pt-4 border-t border-neutral-800">
                <button
                  onClick={onResetSettings}
                  className="flex items-center space-x-1.5 px-3 py-2 text-xs text-red-400 hover:bg-red-950/40 rounded transition"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset All Settings to Factory Defaults</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
